import KitButton from '@omni/kit/components/KitButton';
import KitContainedIcon from '@omni/kit/components/KitContainedIcon';
import KitInput from '@omni/kit/components/KitInput';
import KitInputValidationMessage from '@omni/kit/components/KitInputValidationMessage';
import KitSnack from '@omni/kit/components/KitSnack';
import KitText from '@omni/kit/components/KitText';
import emojiRegex from 'emoji-regex';
import React, {
  ReactElement,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, StyleSheet, TextInput, View } from 'react-native';

import Environment, { IS_LOCALHOST } from '../Environment';
import HealthMonitor from '../HealthMonitor';
import { SUBSPLASH_AUTH_PROVIDER_ID } from '../constants/identifiers';
import { useCaptcha } from '../contexts/GoogleCaptchaContext';
import { useShellContext } from '../contexts/ShellContext';
import useShellAuthProviders from '../contexts/ShellContext/hooks/useShellAuthProviders';
import BorderRadius from '../theming/BorderRadius';
import Colors from '../theming/Colors';
import Spacing from '../theming/Spacing';
import isValidEmail from '../utilities/isValidEmail';
import { handleErrorResponse } from '../utilities/utilities';
import AuthFooter from './AuthFooter';

const AuthenticatorNotFoundScreen = ({
  email,
  firstNameValue,
  lastNameValue,
  phoneAuthenticatorId,
  reset,
  setAuthenticatorNotFoundScreenVisible,
  setConfirmEmailOtpUrl,
  setEmail,
  setEmailOtpAuthState,
  setFirstNameValue,
  setLastNameValue,
  setVerifyCodeScreenVisible,
  showFooter,
  suppressSubsplashBranding,
}: {
  email: string;
  firstNameValue: string;
  lastNameValue: string;
  phoneAuthenticatorId: string;
  reset: () => void;
  setAuthenticatorNotFoundScreenVisible: (visible: boolean) => void;
  setConfirmEmailOtpUrl: (url: string) => void;
  setEmail: (email: string) => void;
  setEmailOtpAuthState: (state: string) => void;
  setFirstNameValue: (firstName: string) => void;
  setLastNameValue: (lastName: string) => void;
  setVerifyCodeScreenVisible: (visible: boolean) => void;
  showFooter: boolean;
  suppressSubsplashBranding: boolean;
}): ReactElement => {
  // ------------------------------------------------------------
  // Context & Custom Hooks

  const regex = emojiRegex();
  const { t } = useTranslation();
  const emailInputRef = useRef<TextInput>(null);
  const { app } = useShellContext();
  const { loginEnabledProviders } = useShellAuthProviders();
  const { fetchCaptcha, siteKey } = useCaptcha();
  const [invalidNameMessage, setInvalidNameMessage] = useState<string>('');
  const [isFirstNameValid, setIsFirstNameValid] = useState<boolean>(true);
  const [isLastNameValid, setIsLastNameValid] = useState<boolean>(true);
  const [invalidEmailMessage, setInvalidEmailMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  const isFormComplete = useMemo((): boolean => {
    return Boolean(firstNameValue?.trim() && lastNameValue?.trim() && email);
  }, [email, firstNameValue, lastNameValue]);

  const hasEmoji = useCallback(
    (text: string): boolean => {
      return regex.test(text);
    },
    [regex]
  );

  const handleNamesErrorMessage = useCallback(() => {
    if (hasEmoji(firstNameValue)) {
      setInvalidNameMessage(t('auth:error.emojisError'));
      setIsFirstNameValid(false);

      return true;
    } else if (hasEmoji(lastNameValue)) {
      setInvalidNameMessage(t('auth:error.emojisError'));
      setIsLastNameValid(false);

      return true;
    } else if (hasEmoji(firstNameValue) && hasEmoji(lastNameValue)) {
      setInvalidNameMessage(t('auth:error.emojisError'));
      setIsFirstNameValid(false);
      setIsLastNameValid(false);

      return true;
    }

    return false;
  }, [firstNameValue, hasEmoji, lastNameValue, t]);

  const sendEmailOtp = async () => {
    if (handleNamesErrorMessage()) {
      return;
    }

    if (!isValidEmail(email)) {
      setInvalidEmailMessage(t('auth:error.invalidEmail'));

      return;
    }

    setIsLoading(true);

    const emailOtpUrl = loginEnabledProviders.find(
      (provider) => provider.authproviderid === SUBSPLASH_AUTH_PROVIDER_ID
    )?.otp_url;

    if (!emailOtpUrl) {
      return;
    }

    /**
     * To enable testing the captcha failure scenario, purposely send mismatched site key.
     * Otherwise, use the site key from the context
     */
    const verificationSiteKey =
      Platform.OS === 'web' && email.includes('+failRecaptchaTest')
        ? Environment.euaRecaptchaSiteKeyTestFailWeb
        : siteKey;

    const response = await fetch(emailOtpUrl, {
      method: 'POST',
      body: JSON.stringify({
        branding_app_key: app.appKey,
        email,
        phone_authenticator_id: phoneAuthenticatorId,
        verification_site_key: verificationSiteKey,
        verification_token: await fetchCaptcha('authorize_email_otp'),
        ...(Platform.OS === 'web' && {
          skip_deep_link: true,
          use_refresh_cookie: !IS_LOCALHOST,
        }),
      }),
    });

    if (response.ok) {
      const responseData = await response.json();

      setAuthenticatorNotFoundScreenVisible(false);
      setConfirmEmailOtpUrl(responseData.confirm_url);
      setEmailOtpAuthState(responseData.state);
      setVerifyCodeScreenVisible(true);
    } else {
      const { status, data } = await handleErrorResponse(response);

      if (status === 500) {
        HealthMonitor.logError(data, {
          tags: {
            label: 'auth:failed to send email OTP',
          },
        });
      }

      KitSnack.show(t('common:oopsSomethingWentWrongPleaseTryAgainLater'));
    }

    setIsLoading(false);
  };

  return (
    <>
      <View style={styles.container}>
        <View style={styles.iconContainer}>
          <KitContainedIcon name='user' size='md' />
        </View>
        <View>
          <KitText
            center
            extraBold
            fontSize={24}
            fontWeight={900}
            style={styles.title}
            color={Colors.N900}
          >
            {t('auth:authenticatorNotFoundTitle')}
          </KitText>
          <KitText center style={styles.subtitle} color={Colors.N900}>
            {t('auth:authenticatorNotFoundSubtitle')}
          </KitText>
        </View>
        <View
          style={{
            marginTop: Spacing.m,
            marginBottom: invalidNameMessage ? 58 : Spacing.m,
          }}
        >
          <KitInputValidationMessage message={invalidNameMessage}>
            <View
              style={{
                flexDirection: 'row',
                gap: Spacing.m,
              }}
              testID='NameContainer'
            >
              <KitInput
                autoCorrect={false}
                autoFocus
                label={t('common:name')}
                onChangeText={(v) => {
                  setFirstNameValue(v);
                  setInvalidNameMessage('');
                  setIsFirstNameValid(true);
                }}
                style={{ flex: 1 }}
                testID='FirstName'
                placeholder={t('common:first')}
                value={firstNameValue}
                isValid={isFirstNameValid}
              />
              <KitInput
                autoCorrect={false}
                label={' '}
                onChangeText={(v) => {
                  setLastNameValue(v);
                  setInvalidNameMessage('');
                  setIsLastNameValid(true);
                }}
                style={{ flex: 1 }}
                testID='LastName'
                placeholder={t('common:last')}
                value={lastNameValue}
                isValid={isLastNameValid}
              />
            </View>
          </KitInputValidationMessage>
        </View>
        <View
          style={{
            marginBottom: invalidEmailMessage ? 58 : Spacing.xl,
          }}
        >
          <KitInputValidationMessage message={invalidEmailMessage}>
            <KitInput
              keyboardType='email-address'
              autoCapitalize='none'
              inputRef={emailInputRef}
              label={t('common:email')}
              onChangeText={(v) => {
                setEmail(v);
                setInvalidEmailMessage('');
              }}
              testID='EmailAddress'
              value={email}
              isValid={!invalidEmailMessage}
              onSubmitEditing={() => sendEmailOtp()}
              blurOnSubmit={true}
              returnKeyType='go'
            />
          </KitInputValidationMessage>
        </View>
        <View style={{ gap: Spacing.m }}>
          <KitButton
            color={Colors.N1000}
            disabled={!isFormComplete || isLoading}
            onPress={sendEmailOtp}
            isLoading={isLoading}
            title={t('common:buttonTitleContinue')}
          />
          <KitButton
            secondary
            onPress={reset}
            title={t('auth:logInAnotherWay')}
          />
        </View>
        <AuthFooter
          showFooter={showFooter}
          suppressSubsplashBranding={suppressSubsplashBranding}
          showRecaptcha={true}
        />
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: Spacing.xl,
    maxWidth: 600,
    width: '100%',
  },
  iconContainer: {
    marginBottom: Spacing.l,
    alignItems: 'center',
  },
  title: {
    textAlign: 'center',
    marginBottom: Spacing.m,
  },
  subtitle: {
    textAlign: 'center',
    marginBottom: Spacing.l,
  },
  errorContainer: {
    paddingHorizontal: Spacing.l,
    paddingVertical: Spacing.m,
    backgroundColor: Colors.R500,
    borderRadius: BorderRadius.m,
    marginBottom: Spacing.l,
    width: '100%',
  },
});

export default AuthenticatorNotFoundScreen;
